import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import "./capability-05.css";
import smartCommunicationVideo from "../../assets/capability/AI Content Editor.mp4";
import { useLanguage } from "../../i18n/LanguageContext";

gsap.registerPlugin(ScrollTrigger);

export default function Capability05() {
  const rootRef = useRef(null);
  const { tr } = useLanguage();

  useEffect(() => {
    const ctx = gsap.context(() => {
      const root = rootRef.current;
      if (!root) return;

      const isMobile = window.innerWidth <= 768;

      // Refresh ScrollTrigger saat resize
      const handleResize = () => {
        ScrollTrigger.refresh();
      };
      window.addEventListener("resize", handleResize);

      // === FADE SCROLL HINT (INTRO) ===
      // Adapted from provided logic
      gsap.to(".cap05-scroll", {
        autoAlpha: 0,
        duration: 0.2,
        scrollTrigger: {
          trigger: document.body,
          start: "top top",
          end: "top top-=1",
          toggleActions: "play none reverse none",
        },
      });

      // === LETTER SCROLL ANIMATION ===
      // Adapted from provided logic
      const hcList = root.querySelector(".cap05-list");
      // #region agent log
      const log = (message, data, hypothesisId) => {};
      if (hcList && !isMobile) {
        const rect = hcList.getBoundingClientRect();
        log("cap05 letter ST setup", { hasHcList: !!hcList, isMobile, rectTop: rect.top, rectBottom: rect.bottom, vh: window.innerHeight }, "A");
      }
      // #endregion
      if (hcList && !isMobile) {
        const slideHeight = window.innerHeight;
        const tween = gsap.to(".cap05-letter", {
          yPercent: 100,
          ease: "power1.inOut",
          scrollTrigger: {
            trigger: root,
            start: "top top",
            end: `+=${slideHeight}`,
            scrub: 1,
            invalidateOnRefresh: true,
            // #region agent log
            onUpdate: (self) => {
              if (self.progress > 0.9 || self.progress < 0.1) log("cap05 letter progress", { progress: self.progress, direction: self.direction }, "A");
            },
            // #endregion
          },
          stagger: {
            each: 0.05,
            from: "random",
          },
        });
        // #region agent log
        const st = tween.scrollTrigger;
        if (st) log("cap05 letter ST range", { start: st.start, end: st.end, triggerRectTop: hcList.getBoundingClientRect().top }, "B");
        // #endregion
      }

      // === SLIDE PPT STYLE (like capability.jsx) ===
      // Set initial state
      gsap.set(root, { autoAlpha: 1, y: 0 });

      if (hcList) {
        gsap.set(hcList, { opacity: 1, y: 0 });
      }

      // Mobile: Skip pinning and complex fade animations
      if (isMobile) {
        // Mobile: no title animation

        const slideHeight = window.innerHeight;

        // Pin section for full viewport height (like PowerPoint)
        ScrollTrigger.create({
          trigger: root,
          start: "top top",
          end: `+=${slideHeight}`,
          pin: root,
          pinSpacing: true,
          scrub: false,
        });
      } else {
        // Desktop: Full pinning and fade animations
      const slideHeight = window.innerHeight;

      // Pin section for full viewport height (like PowerPoint)
      ScrollTrigger.create({
        trigger: root,
        start: "top top",
        end: `+=${slideHeight}`,
        pin: root,
        pinSpacing: true,
        scrub: false,
      });

      // Fade in animation when section enters viewport (support scroll up)
        // Only fade content, not background
        if (hcList) {
      gsap.fromTo(
            hcList,
            { opacity: 0, y: 30 },
        {
              opacity: 1,
          y: 0,
          duration: 1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: root,
            start: "top 80%",
            end: "top 20%",
            scrub: true,
            invalidateOnRefresh: true,
          },
        }
      );
        }

      // Fade out animation when section exits (support scroll up)
        // Only fade content, keep background visible to prevent black screen
        if (hcList) {
      gsap.fromTo(
            hcList,
            { opacity: 1, y: 0 },
        {
              opacity: 0,
          y: -30,
          duration: 1,
          ease: "power2.in",
          scrollTrigger: {
            trigger: root,
            start: "bottom top",
            end: `bottom+=${slideHeight * 0.5} top`,
            scrub: true,
            invalidateOnRefresh: true,
          },
        }
      );
        }
      }

      // Refresh setelah setup
      ScrollTrigger.refresh();

      return () => {
        window.removeEventListener("resize", handleResize);
      };
    }, rootRef);

    return () => ctx.revert();
  }, []);

  return (
    <div style={{ backgroundColor: "#D8D8D8" }}>
      <section id="solutions-05" className="cap05-section" ref={rootRef}>
        

        <div className="cap05-header">
          <ul className="cap05-list cap05-list-horizontal">
            <li>
              {tr("AI", "AI").split("").map((char, i) => (
                <span key={i} className="cap05-letter">
                <span>{char}</span>
                <span>{char}</span>
              </span>
            ))}
          </li>

            <li>
              {tr("Content", "Konten").split("").map((char, i) => (
                  <span key={i} className="cap05-letter">
                  <span>{char}</span>
                  <span>{char}</span>
                </span>
              ))}
          </li>

            <li>
              {tr("Editor", "Editor").split("").map((char, i) => (
                <span key={i} className="cap05-letter">
                  <span>{char}</span>
                  <span>{char}</span>
                </span>
              ))}
            </li>
        </ul>
        </div>
        
        <p className="cap05-paragraph">
          {tr(
            "Turn raw recordings or loose ideas into polished videos, posts, or audio - complete with AI-assisted edits, cuts, and audio/video enhancements",
            "Ubah rekaman mentah atau ide kasar menjadi video, post, atau audio yang rapi — lengkap dengan edit, cut, dan peningkatan audio/video berbasis AI"
          )}
        </p>
        <p className="cap05-paragraph cap05-note">
          {tr(
            "Perfect for content creators who want the editing process in seconds",
            "Cocok untuk content creator yang ingin proses editing dalam hitungan detik"
          )}
        </p>
        <p className="cap05-num cap05-num-bottom">(05)</p>
        <video
          className="cap05-video"
          src={smartCommunicationVideo}
          autoPlay
          loop
          muted
          playsInline
        />
      </section>
    </div>

  );
}
